import { useCallback, useMemo } from "react";
import { useConstant, useToggle } from "../../../../hooks/index.js";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $insertNodes } from "lexical";
import { useEditorCSSRules } from "../../theme/index.js";
function useCreateEditorContext(props) {
  const { error, disabled = false, initialToolbarVisible = true, editorThemeStyles } = props;
  const [editor] = useLexicalComposerContext();
  const wormholeTargets = useConstant(() => ({
    toolbarTarget: window.crypto.randomUUID(),
    quickInsertTarget: window.crypto.randomUUID(),
    footerActionTarget: window.crypto.randomUUID(),
    footerContentTarget: window.crypto.randomUUID(),
    childrenTarget: window.crypto.randomUUID()
  }));
  const [toolbarVisible, toggleToolbarVisibility] = useToggle(initialToolbarVisible);
  const cssRules = useEditorCSSRules(editorThemeStyles);
  const toHTML = useCallback(() => {
    const editorState = editor.getEditorState();
    return editorState.read(() => {
      return {
        html: $generateHtmlFromNodes(editor),
        css: cssRules
      };
    });
  }, [cssRules, editor]);
  const fromHTML = useCallback(
    (htmlString) => {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlString, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.select();
        $insertNodes(nodes);
      });
    },
    [editor]
  );
  const toJSON = useCallback(() => {
    const editorState = editor.getEditorState();
    return {
      json: editorState.toJSON(),
      css: cssRules
    };
  }, [cssRules, editor]);
  const fromJSON = useCallback(
    (json) => {
      editor.update(() => {
        editor.setEditorState(editor.parseEditorState(json));
      });
    },
    [editor]
  );
  const toPlainText = useCallback(() => {
    return editor.getRootElement()?.textContent ?? "";
  }, [editor]);
  const api = useMemo(
    () => ({
      toHTML,
      toJSON,
      toString: toPlainText,
      fromHTML,
      fromJSON,
      toggleToolbarVisibility
    }),
    [fromHTML, fromJSON, toHTML, toJSON, toPlainText, toggleToolbarVisibility]
  );
  const data = useMemo(
    () => ({
      error,
      cssRules,
      disabled,
      toolbarVisible,
      ...wormholeTargets
    }),
    [error, cssRules, disabled, toolbarVisible, wormholeTargets]
  );
  return { api, data };
}
export {
  useCreateEditorContext
};
//# sourceMappingURL=useCreateEditorContext.js.map
